import {
  Box,
  Button,
  FormControlLabel,
  Popover,
  type PopoverProps,
  Radio,
  Stack,
} from '@mui/material';
import { isValid } from 'date-fns';
import type { FocusEvent } from 'react';
import { DatePickerWithEventTracking } from '@/components/DatePicker/DatePickerV2';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import {
  DATE_RANGE_OPTION_LABELS,
  DATE_RANGE_OPTIONS,
  type DateInput,
} from '../types';
import { getEndDate, getStartDate } from './DatesFilter.helpers';

interface Props extends PopoverProps {
  selectedDateRange: DATE_RANGE_OPTIONS;
  endDate: DateInput;
  onChangeDateRange: (selectedDateRange: DATE_RANGE_OPTIONS) => void;
  onChangeEndDate: (date: DateInput | null) => void;
  onChangeStartDate: (date: DateInput | null) => void;
  startDate: DateInput;
  onClose: () => void;
}

export const DatesFilterDropdown = ({
  selectedDateRange,
  endDate: customEndDate,
  onChangeDateRange,
  onChangeEndDate,
  onChangeStartDate,
  startDate: customStartDate,
  onClose,
  ...rest
}: Props) => {
  const areDatePickersEnabled = selectedDateRange === DATE_RANGE_OPTIONS.CUSTOM;
  const trackEvent = useTrackHomePageAnalytics();

  const startDate = getStartDate({
    dateRange: selectedDateRange,
    customStartDateValue: customStartDate,
  });
  const endDate = getEndDate({
    dateRange: selectedDateRange,
    customEndDateValue: customEndDate,
  });

  return (
    <Popover
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={onClose}
      {...rest}
    >
      <Stack
        role="radiogroup"
        aria-label="Created at date range options"
        sx={{
          m: 1.5,
          mb: 3,
          flexDirection: 'column',
          gap: 0,
        }}
      >
        <FormControlLabel
          control={
            <Radio
              checked={selectedDateRange === DATE_RANGE_OPTIONS.DEFAULT}
              onClick={() => {
                trackEvent(
                  TrackHomePageEvent.FactsAndFiguresDateFilterTypeSelect,
                  {
                    dateType: DATE_RANGE_OPTIONS.DEFAULT,
                  },
                );

                onChangeDateRange(DATE_RANGE_OPTIONS.DEFAULT);
                onChangeStartDate(null);
                onChangeEndDate(null);
              }}
            />
          }
          label={DATE_RANGE_OPTION_LABELS[DATE_RANGE_OPTIONS.DEFAULT]}
          slotProps={{ typography: { variant: 'body2' } }}
        />

        <FormControlLabel
          control={
            <Radio
              checked={selectedDateRange === DATE_RANGE_OPTIONS.LTM}
              onClick={() => {
                trackEvent(
                  TrackHomePageEvent.FactsAndFiguresDateFilterTypeSelect,
                  {
                    dateType: DATE_RANGE_OPTIONS.LTM,
                  },
                );

                onChangeDateRange(DATE_RANGE_OPTIONS.LTM);
                onChangeStartDate(null);
                onChangeEndDate(null);
              }}
            />
          }
          label={DATE_RANGE_OPTION_LABELS[DATE_RANGE_OPTIONS.LTM]}
          slotProps={{ typography: { variant: 'body2' } }}
        />

        <FormControlLabel
          control={
            <Radio
              checked={selectedDateRange === DATE_RANGE_OPTIONS.LTD}
              onClick={() => {
                trackEvent(
                  TrackHomePageEvent.FactsAndFiguresDateFilterTypeSelect,
                  {
                    dateType: DATE_RANGE_OPTIONS.LTD,
                  },
                );

                onChangeDateRange(DATE_RANGE_OPTIONS.LTD);
                onChangeStartDate(null);
                onChangeEndDate(null);
              }}
            />
          }
          label={DATE_RANGE_OPTION_LABELS[DATE_RANGE_OPTIONS.LTD]}
          slotProps={{ typography: { variant: 'body2' } }}
        />

        <FormControlLabel
          control={
            <Radio
              checked={selectedDateRange === DATE_RANGE_OPTIONS.YTD}
              onClick={() => {
                trackEvent(
                  TrackHomePageEvent.FactsAndFiguresDateFilterTypeSelect,
                  {
                    dateType: DATE_RANGE_OPTIONS.YTD,
                  },
                );

                onChangeDateRange(DATE_RANGE_OPTIONS.YTD);
                onChangeStartDate(null);
                onChangeEndDate(null);
              }}
            />
          }
          label={DATE_RANGE_OPTION_LABELS[DATE_RANGE_OPTIONS.YTD]}
          slotProps={{ typography: { variant: 'body2' } }}
        />

        <FormControlLabel
          control={
            <Radio
              checked={selectedDateRange === DATE_RANGE_OPTIONS.CUSTOM}
              onClick={() => {
                trackEvent(
                  TrackHomePageEvent.FactsAndFiguresDateFilterTypeSelect,
                  {
                    dateType: DATE_RANGE_OPTIONS.CUSTOM,
                  },
                );

                onChangeDateRange(DATE_RANGE_OPTIONS.CUSTOM);
              }}
            />
          }
          label={DATE_RANGE_OPTION_LABELS[DATE_RANGE_OPTIONS.CUSTOM]}
          slotProps={{ typography: { variant: 'body2' } }}
          sx={{ pb: 1 }}
        />

        <Stack
          sx={{
            marginLeft: 4,
          }}
        >
          <DatePickerWithEventTracking
            eventContainer="date-filter-dropdown-first"
            onAccept={(newDate) => {
              trackEvent(
                TrackHomePageEvent.FactsAndFiguresDateFilterStartDateInput,
                {
                  dateValue: newDate,
                },
              );
            }}
            onChange={(newDate) => {
              onChangeStartDate(newDate);
            }}
            disabled={!areDatePickersEnabled}
            slotProps={{
              textField: {
                label: 'Start Date',
                required: areDatePickersEnabled,
                sx: { width: '100%', pb: 2 },
                onBlur: (event: FocusEvent<HTMLInputElement>) => {
                  const newDate = new Date(event.target.value);

                  if (isValid(newDate)) {
                    trackEvent(
                      TrackHomePageEvent.FactsAndFiguresDateFilterStartDateInput,
                      {
                        dateValue: newDate.toISOString(),
                      },
                    );
                  }
                },
              },
            }}
            value={startDate ? new Date(startDate) : null}
          />
          <DatePickerWithEventTracking
            eventContainer="date-filter-dropdown-second"
            onAccept={(newDate) => {
              trackEvent(
                TrackHomePageEvent.FactsAndFiguresDateFilterEndDateInput,
                {
                  dateValue: newDate,
                },
              );
            }}
            onChange={(newDate) => {
              onChangeEndDate(newDate);
            }}
            disabled={!areDatePickersEnabled}
            slotProps={{
              textField: {
                label: 'End Date',
                required: areDatePickersEnabled,
                sx: { width: '100%' },
                onBlur: (event: FocusEvent<HTMLInputElement>) => {
                  const newDate = new Date(event.target.value);

                  if (isValid(newDate)) {
                    trackEvent(
                      TrackHomePageEvent.FactsAndFiguresDateFilterEndDateInput,
                      {
                        dateValue: newDate.toISOString(),
                      },
                    );
                  }
                },
              },
            }}
            value={endDate ? new Date(endDate) : null}
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          borderTop: (t) => `1px solid ${t.palette.other.outlineBorder}`,
          display: 'flex',
          flexDirection: 'row-reverse',
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            trackEvent(TrackHomePageEvent.FactsAndFiguresDateFilterDoneClick, {
              dateType: selectedDateRange,
              startDate,
              endDate,
            });

            onClose();
          }}
          sx={{
            my: 1,
            mx: 2,
          }}
        >
          Done
        </Button>
      </Box>
    </Popover>
  );
};
